import type Highcharts from "highcharts";
import React from "react";
import * as io from "io-ts";

import useAnalysisXAxis from "../../../../../../hooks/useAnalysisXAxis";
import { renderToString } from "react-dom/server";
import useQuantityYAxes, { getAxisIds } from "../../../../../../../Chart/hooks/useQuantityYAxes";
import type { SelectedQuantity } from "../../../../../../../useSelectedQuantities";
import { quantityColor } from "../../../../../../../useSelectedQuantities";
import validateInterface from "@ui/lib/validateInterface";
import {
  // MeasurePointMetadata,
  MEASURE_POINT_METADATA,
  PointMetadata,
  QUANTITY_FLAG_METADATA,
} from "../../../../../../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import { getMainAxisSelectedPoints } from "@ui/Highcharts/plugins/stickyTooltip";
import { useQuantityNoDataIntervals } from "../../../../../../../useNoDataIntervals";
import t from "../../../../../../../../../i18n";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import { DEFAULT_MARGIN_RIGHT_PX } from "@ui/Highcharts/BetterHighcharts";
import { FLAG_AXIS } from "../../../../../../../Chart/series/getQuantityFlagSerie";
import type { Project } from "../../../../../../../../../api/useProjects";
import type { Source } from "../../../../../../../../../api/useSources";
import useTooltipClick from "@ui/Highcharts/hooks/useTooltipClick";
import useUpdateMetrologyOverrides from "../../../../../../../Chart/useUpdateMetrologyOverrides";
import type { Analysis } from "../../../../../../../../../api/Analysis";
import { createQuantityThresholdPlotLine } from "../../../../../../../Chart/series/createQuantityThresholdPlotLine";
import useToasts from "@ui/Toast/useToasts";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import type { MeasuresChartType } from "../../../../../../../Measures/MeasuresChartType";
import { color } from "@ui";
import useActivationFilters from "../../../../../../../../../api/useActivationFilters";
import type { ActiveAnalysis } from "../../../../../../../../../api/useActiveAnalysesStream";
import useGroupBreaks from "../../../../../../../Chart/hooks/useGroupBreaks";
import type { Action } from "@ui/Highcharts/ui/Tooltip";
import formatNumber from "@ui/i18n/formatNumber";
import { useQuantityByName } from "../../../../../../../../../api/useQuantities";
import {
  QuantityValidityTooltipAction as TooltipAction,
  ValidityQuantityChartTooltip,
} from "../../../../../../../Chart/ChartTooltip/data_symptom/ValidityQuantityChartTooltip";
import Text from "@ui/Text";
import { QuantityChartTooltip } from "../../../../../../../Chart/ChartTooltip/data_symptom/QuantityChartTooltip";
import { ChartTooltip } from "../../../../../../../Chart/ChartTooltip";
import { AnalysisDateChartTooltip } from "../../../../../../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FlagChartTooltip } from "../../../../../../../Chart/ChartTooltip/common/FlagChartTooltip";
import {
  DIRECT_INVALID_STATUSES,
  INVALID_STATUSES,
  VALID_STATUSES,
} from "diagnostics-domain/quantity/metrology";
import type { Event } from "../../../../../../../../../api/useEvents";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../../../../../../Chart/hooks/useAnalysisSelectionPlotLines";
import {
  useFormulaVersions,
  type FormulaVersion,
} from "../../../../../../../../../api/quantity/formula-version";
import {
  getBaseFormulaApplicationPeriod,
  getFormulaVersionApplicationPeriod,
  getFormulaVersionAt,
} from "../../../../../../../../../domain/formula-version";

enum AnalysisFilterPlotBand {
  Min = "ANALYSIS_FILTER_MIN_BAND",
  Max = "ANALYSIS_FILTER_MAX_BAND",
  MaxStd = "ANALYSIS_FILTER_MAX_STD_BAND",
}

const useOptions = ({
  chartRef,
  selectedQuantities,
  series,
  analyses,
  activeAnalyses,
  project,
  source,
  timeZone,
  axisGroupedByUnit,
  multiColor = false,
  canUpdateQuantityValues,
  selectedChartOption,
  allAnalysesMode = false,
  event,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  selectedQuantities: SelectedQuantity[];
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
  analyses: Analysis[];
  activeAnalyses: ActiveAnalysis[];
  project: Project;
  source: Source;
  timeZone: string;
  axisGroupedByUnit: boolean;
  multiColor?: boolean;
  canUpdateQuantityValues: boolean;
  period: Period;
  selectedChartOption?: MeasuresChartType;
  allAnalysesMode?: boolean;
  event: Event;
}): Options => {
  const quantityByName = useQuantityByName(project.id);
  const canUpdateQuantityValuesRef = React.useRef(canUpdateQuantityValues);
  const activationFilters = useActivationFilters(project.id);
  canUpdateQuantityValuesRef.current = canUpdateQuantityValues;
  const yAxisIds = React.useMemo(
    () => getAxisIds(selectedQuantities, axisGroupedByUnit),
    [selectedQuantities, axisGroupedByUnit],
  );
  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId: yAxisIds,
  });

  // Need to change this behavior to handle source / active analyses from all data mode
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: allAnalysesMode ? "analyses" : "active-analyses",
    event,
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useQuantityNoDataIntervals(
      activeAnalyses,
      selectedQuantities,
      selectedChartOption ?? "measures",
      project.id,
    ),
  });

  const { addToast } = useToasts();

  const updateMetrologyOverrides = useUpdateMetrologyOverrides(project.id);

  useTooltipClick(
    chartRef,
    React.useCallback(
      (action: TooltipAction, points: Highcharts.Point[]) => {
        if (action === TooltipAction.Validate || action === TooltipAction.Invalidate) {
          void updateMetrologyOverrides({
            addToast,
            points,
            isValid: action === TooltipAction.Validate,
          });
        } else {
          throw new Error(action satisfies never);
        }
      },
      [addToast, updateMetrologyOverrides],
    ),
  );

  const yAxes = useQuantityYAxes({
    selectedQuantities,
    axisGroupedByUnit,
    multiColor,
    threshold: selectedQuantities.length === 1,
    selectedChartOption,
  });
  const multiAxes = yAxes.length > 1;

  const formulaVersions = useFormulaVersions(project.id);

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const selectedPoints = getMainAxisSelectedPoints(this.series.chart);

      const selectedData = validateInterface(
        io.array(PointMetadata),
        selectedPoints.map(p => p.options.custom),
      );
      const hasSelectedValid = selectedData.some(
        p => "validity" in p && VALID_STATUSES.has(p.validity),
      );

      const hasSelectedInvalid = selectedData.some(
        p => "validity" in p && DIRECT_INVALID_STATUSES.has(p.validity),
      );

      const metadata = validateInterface(PointMetadata, this.point.options.custom);
      const quantityFlags = metadata.type === QUANTITY_FLAG_METADATA ? metadata.flags : undefined;
      const human = metadata.type === MEASURE_POINT_METADATA ? metadata.human : null;
      const value = selectedChartOption === "value" ? (human?.value ?? this.point.y) : this.point.y;

      const validity = metadata.type === MEASURE_POINT_METADATA ? metadata.validity : null;
      const isInvalid = validity ? INVALID_STATUSES.has(validity) : null;
      const isPartial = validity === "VALID_PARTIAL_INPUTS";

      const quantity = quantityByName[this.series.name];
      const hasQuantityField = quantity !== undefined;

      const quantityFieldLabel =
        selectedChartOption === "value"
          ? t("measures.chart.tooltip.quantity_field.label")
          : t("measures.chart.tooltip.std");
      const quantityFieldValue =
        value === undefined
          ? "-"
          : formatNumber(value as number, {
              sign: human?.sign ?? undefined,
              precision: human?.precision ?? undefined,
              unit: human?.unit ?? undefined,
            });
      const option = {
        label: quantityFieldLabel,
        value: quantityFieldValue,
      };
      const actionDescription = this.point.description;
      const actions: Action<TooltipAction>[] = [
        {
          text: t("measures.chart.tooltip.button.validate"),
          action: TooltipAction.Validate,
          intent: "confirm",
          show: canUpdateQuantityValuesRef.current && hasSelectedInvalid,
        },
        {
          text: t("measures.chart.tooltip.button.invalidate"),
          action: TooltipAction.Invalidate,
          intent: "danger",
          show: canUpdateQuantityValuesRef.current && hasSelectedValid,
        },
      ];
      const selectedCount = this.point.selected ? selectedPoints.length : 0;
      let formulaVersion: FormulaVersion | null = null;
      let formulaApplicationPeriod: { from: Date; to: Date | null } | null = null;
      if (quantity) {
        const quantityFormulaVersions = formulaVersions.filter(fv => fv.quantityId === quantity.id);
        formulaVersion = getFormulaVersionAt(quantity.id, formulaVersions, new Date(this.point.x));
        if (formulaVersion) {
          formulaApplicationPeriod = getFormulaVersionApplicationPeriod(
            formulaVersion,
            quantityFormulaVersions,
          );
        } else {
          if (quantityFormulaVersions.length === 0) {
            formulaApplicationPeriod = null;
          } else {
            const startsAt =
              source.minSnapshotDate ?? project.analysesAggregate?.earliestActiveAnalysis?.date;
            formulaApplicationPeriod = getBaseFormulaApplicationPeriod(
              startsAt ? new Date(startsAt) : new Date(),
              quantityFormulaVersions,
            );
          }
        }
      }

      return renderToString(
        <ChartTooltip>
          {selectedCount > 1 ? (
            <Text type="h4">
              {t("measures.chart.tooltip.selectedPoints", {
                count: selectedPoints.length,
              })}
            </Text>
          ) : (
            <>
              <AnalysisDateChartTooltip
                date={this.point.x}
                group={metadata.group}
                label={metadata.label}
                timezone={timeZone}
              />
              {hasQuantityField ? (
                <QuantityChartTooltip
                  name={quantity.name}
                  description={quantity.description}
                  formula={formulaVersion?.formula ?? quantity.formula}
                  formulaApplicationPeriod={formulaApplicationPeriod}
                  timezone={timeZone}
                  option={option}
                />
              ) : null}
            </>
          )}
          {quantityFlags ? <FlagChartTooltip flags={quantityFlags} /> : null}
          {actionDescription ? (
            <ValidityQuantityChartTooltip
              actionDescription={actionDescription}
              actions={actions}
              isInvalid={isInvalid}
              isPartial={isPartial}
              selectedCount={selectedCount}
            />
          ) : null}
        </ChartTooltip>,
      );
    },
    [
      selectedChartOption,
      quantityByName,
      timeZone,
      formulaVersions,
      source.minSnapshotDate,
      project.analysesAggregate?.earliestActiveAnalysis?.date,
    ],
  );

  const handleSeriesMouseOver = React.useCallback<Highcharts.SeriesMouseOverCallbackFunction>(
    function () {
      const selectedQuantity = selectedQuantities.find(({ quantity }) => {
        if (typeof this.userOptions.name === "string")
          return this.userOptions.name === quantity.name;
        return false;
      });

      if (!selectedQuantity) return;

      const { colorIndex, quantity } = selectedQuantity;

      const color = quantityColor(colorIndex);

      if (typeof quantity.minMetrology === "number" && selectedChartOption === "value")
        this.yAxis.addPlotLine(createQuantityThresholdPlotLine(quantity, "min", color));

      if (typeof quantity.maxMetrology === "number" && selectedChartOption === "value")
        this.yAxis.addPlotLine(createQuantityThresholdPlotLine(quantity, "max", color));

      if (typeof quantity.maxStdMetrology === "number" && selectedChartOption === "std")
        this.yAxis.addPlotLine(createQuantityThresholdPlotLine(quantity, "maxStd", color));
    },
    [selectedQuantities, selectedChartOption],
  );

  const handleSeriesMouseOut = React.useCallback<Highcharts.SeriesMouseOutCallbackFunction>(
    function () {
      for (const plot of this.yAxis.plotLinesAndBands || []) {
        this.yAxis.removePlotLine(plot.id);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (selectedQuantities.length === 1 && chartRef.current) {
      const { quantity } = selectedQuantities[0] || {};

      if (!quantity) return;

      const yAxis = chartRef.current?.yAxis.find(({ userOptions }) =>
        userOptions.id?.includes("axis-quantity"),
      );

      if (!yAxis) return;

      const analysisFilter = activationFilters.find(({ quantityId }) => quantityId === quantity.id);

      yAxis.removePlotBand(AnalysisFilterPlotBand.Min);
      yAxis.removePlotBand(AnalysisFilterPlotBand.Max);
      yAxis.removePlotBand(AnalysisFilterPlotBand.MaxStd);

      if (selectedChartOption === "value" && analysisFilter) {
        if (analysisFilter.min !== null)
          yAxis.addPlotBand({
            id: AnalysisFilterPlotBand.Min,
            from: analysisFilter.min,
            to: Number.MIN_SAFE_INTEGER,
            color: color.raw.phosphorus50,
          });

        if (analysisFilter.max !== null)
          yAxis.addPlotBand({
            id: AnalysisFilterPlotBand.Max,
            from: analysisFilter.max,
            to: Number.MAX_SAFE_INTEGER,
            color: color.raw.phosphorus50,
          });
      } else if (selectedChartOption === "std" && analysisFilter) {
        if (analysisFilter.maxStd !== null)
          yAxis.addPlotBand({
            id: AnalysisFilterPlotBand.MaxStd,
            from: analysisFilter.maxStd,
            to: Number.MAX_SAFE_INTEGER,
            color: color.raw.phosphorus50,
          });
      }
    }
  }, [selectedQuantities, chartRef, activationFilters, selectedChartOption]);

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: initialYAxisOptions,
      },
      chart: {
        zoomType: multiAxes ? "x" : "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
        marginRight: multiAxes ? undefined : DEFAULT_MARGIN_RIGHT_PX, // we need auto-margin back when there is a yAxis label on the right
        panning: { enabled: true, type: selectedQuantities.length > 1 ? "x" : "xy" },
      },
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [...yAxes, FLAG_AXIS],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      plotOptions: {
        series: {
          events:
            selectedQuantities.length > 1
              ? {
                  mouseOver: handleSeriesMouseOver,
                  mouseOut: handleSeriesMouseOut,
                }
              : {},
        },
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
      series,
    };
    return _;
  }, [
    initialXAxisOptions,
    initialYAxisOptions,
    multiAxes,
    selectedQuantities.length,
    xBreaks,
    yAxes,
    constantTooltipFormatter,
    handleSeriesMouseOver,
    handleSeriesMouseOut,
    series,
  ]);
};

export default useOptions;
